import type { FeedbackSubmissionInput, PublicProjectConfig } from '@feedback/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * All calls use the native fetch captured at module load. The network collector
 * patches `window.fetch`, and routing our own traffic through the patched copy
 * would mean a failed submission records itself as a failed request.
 */
const nativeFetch: typeof fetch = globalThis.fetch.bind(globalThis);

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

export async function fetchProjectConfig(
  apiBase: string,
  projectId: string,
): Promise<PublicProjectConfig> {
  const url = `${apiBase}/api/v1/config?project=${encodeURIComponent(projectId)}`;
  const response = await nativeFetch(url, { method: 'GET', mode: 'cors' });
  if (!response.ok) throw new ApiError(await readError(response), response.status);
  return (await response.json()) as PublicProjectConfig;
}

export interface SubmitResult {
  issueUrl: string;
  issueNumber: number;
}

export async function submitFeedback(
  apiBase: string,
  payload: FeedbackSubmissionInput,
): Promise<SubmitResult> {
  const response = await nativeFetch(`${apiBase}/api/v1/feedback`, {
    method: 'POST',
    mode: 'cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new ApiError(await readError(response), response.status);

  const body = (await response.json()) as SubmitResult;
  return { issueUrl: body.issueUrl, issueNumber: body.issueNumber };
}
